import ViewList, { ViewListItem } from "@/components/layout/ViewList";
import { useViewList } from "@/context/ViewListContext";
import { useCourseFromEbook } from "@/hooks/api/course/useGenerateCourse";
import { useSaveCourse } from "@/hooks/api/course/useSaveCourse";
import { CourseCreatedStep } from "@/partials/create/course/CourseCreatedStep";
import { CourseGenerationStep } from "@/partials/create/course/CourseGenerationStep/CourseGenerationStep";
import { CourseReviewStep } from "@/partials/create/course/CourseReviewStep/CourseReviewStep";
import { CreateCharacterStep } from "@/partials/create/course/CreateCharacterStep/CreateCharacterStep";
import { AddContentStep } from "@/partials/create/course/linkedin/AddContentStep";
import { LinkedInStepper } from "@/partials/create/course/linkedin/LinkedInStepper";
import { useEffect, useState } from "react";
import { RouteOptionsById, RouterIds } from "@vixi/router";
import { CourseEditorContextProvider, useUiStore } from "@vixi/ui";
import { Course } from "@vixi/types";

// Constants
const routeId = "/_app/create/linkedin/flow" satisfies RouterIds;
const firstStep = 0;

export const CreateLinkedinCourseFlowPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
};

function RouteComponent() {
  // useGoBack used inside of children components. Not needed here
  const stepperFlow = useViewList({
    initialStep: firstStep,
  });
  const { activeStep, nextStep } = stepperFlow;

  // Stepper
  const {
    header: { setChildren: setHeaderChildren },
    sidebar: { setDisabled: setSidebarDisabled },
  } = useUiStore();

  useEffect(() => {
    setHeaderChildren(<LinkedInStepper activeStep={activeStep} />);
    setSidebarDisabled(() => true);

    return () => {
      setHeaderChildren(null);
      setSidebarDisabled(() => false);
    };
  }, [activeStep]);

  // Handle course generation
  const generateCourseMutation = useCourseFromEbook();
  const { mutateAsync: generateCourse, isSuccess: isCourseGenerated } = generateCourseMutation;

  // Save Course
  const saveCourseMutation = useSaveCourse();
  const { data: courseId } = saveCourseMutation;

  // Store
  const [courseData, setCourseData] = useState<Course | null | undefined>(null);
  useEffect(() => {
    if (import.meta.env.DEV) {
      if (firstStep > 0) {
        generateCourse({
          lang: "en",
          pdfFiles: null as unknown as File[],
        }).then(setCourseData);
        return;
      }
    }

    setCourseData({
      chapters: [],
    });
  }, []);

  return (
    <CourseEditorContextProvider courseData={courseData} setCourseData={setCourseData}>
      <ViewList {...stepperFlow} debug="MainFlow">
        {/* Course Info */}
        <ViewListItem>
          <AddContentStep />
        </ViewListItem>

        {/* Course Generation */}
        <ViewListItem>
          <div className="m-auto">
            <CourseGenerationStep retry={() => {}} uploadMutation={generateCourseMutation} />
          </div>
        </ViewListItem>

        {/* Create Character */}
        <ViewListItem>
          <CreateCharacterStep />
        </ViewListItem>

        {/* Course Review */}
        <ViewListItem>
          {isCourseGenerated && courseData && (
            <CourseReviewStep saveCourseMutation={saveCourseMutation} />
          )}
        </ViewListItem>

        {/* Course Created */}
        <ViewListItem>
          <CourseCreatedStep courseId={courseId} />,
        </ViewListItem>
      </ViewList>
    </CourseEditorContextProvider>
  );
}
